import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

/** Studio-wide saved default view for Studio Portal → All Classes. */
export interface ClassListView {
  sortBy: string | null;
  sortOrder: 'asc' | 'desc';
  hiddenColumns: Array<string>;
  columnOrder: Array<string>;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type SaveClassListViewParams = Pick<
  ClassListView,
  'sortBy' | 'sortOrder' | 'hiddenColumns' | 'columnOrder'
>;

const asStringArray = (value: unknown): Array<string> =>
  Array.isArray(value)
    ? value.filter((id): id is string => typeof id === 'string')
    : [];

/**
 * Reads `{ view }` from the class-list-view endpoints. Anything else (e.g. an
 * older backend without the route) resolves to "no saved view".
 */
export const parseClassListViewResponse = (
  data: unknown,
): ClassListView | null => {
  if (!data || typeof data !== 'object' || !('view' in data)) return null;
  const { view } = data;
  if (!view || typeof view !== 'object') return null;
  const raw = view as Record<string, unknown>;
  const sortBy = typeof raw.sortBy === 'string' ? raw.sortBy : null;
  return {
    sortBy,
    sortOrder: sortBy && raw.sortOrder === 'desc' ? 'desc' : 'asc',
    hiddenColumns: asStringArray(raw.hiddenColumns),
    columnOrder: asStringArray(raw.columnOrder),
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : null,
    updatedBy: typeof raw.updatedBy === 'string' ? raw.updatedBy : null,
  };
};

export const getClassListViewAPI = async () => {
  const response = await baseAPI.get<unknown>('/studios/class-list-view');
  return parseClassListViewResponse(response.data);
};

export const classListViewQueryOptions = queryOptions({
  queryKey: [KEYS.CLASS_LIST_VIEW],
  queryFn: getClassListViewAPI,
  retry: false,
  staleTime: 5 * 60 * 1000,
});

export const useGetClassListView = () => {
  return useQuery(classListViewQueryOptions);
};

export const saveClassListViewAPI = async (params: SaveClassListViewParams) => {
  const response = await baseAPI.put<unknown>(
    '/studios/class-list-view',
    params,
  );
  return parseClassListViewResponse(response.data);
};

export const clearClassListViewAPI = async () => {
  await baseAPI.delete('/studios/class-list-view');
  return null;
};

export const useSaveClassListView = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveClassListViewAPI,
    onSuccess: (view) => {
      queryClient.setQueryData([KEYS.CLASS_LIST_VIEW], view);
    },
  });
};

export const useClearClassListView = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clearClassListViewAPI,
    onSuccess: () => {
      queryClient.setQueryData([KEYS.CLASS_LIST_VIEW], null);
    },
  });
};
